import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';
import AddFacePage from './pages/AddFacePage';
import RecognizePage from './pages/RecognizePage';
import PersonsPage from './pages/PersonsPage';
import WidgetRecognizePage from './pages/WidgetRecognizePage';
import WidgetRegisterPage from './pages/WidgetRegisterPage';
import LoginPage from './pages/LoginPage';

// Set base URL for API calls
axios.defaults.baseURL = process.env.REACT_APP_API_URL || '';

// Configure axios to include auth token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const styles = {
  app: {
    minHeight: '100vh',
    color: '#fff',
  },
  nav: {
    background: 'rgba(0, 0, 0, 0.3)',
    padding: '1rem 2rem',
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  logo: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#00d9ff',
    textDecoration: 'none',
  },
  navLinks: {
    display: 'flex',
    gap: '1rem',
    marginLeft: 'auto',
  },
  navLink: {
    color: '#fff',
    textDecoration: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '8px',
    transition: 'all 0.3s ease',
    fontSize: '0.95rem',
  },
  navLinkActive: {
    background: 'rgba(0, 217, 255, 0.2)',
    color: '#00d9ff',
  },
  main: {
    padding: '2rem',
    maxWidth: '1400px',
    margin: '0 auto',
  },
};

function NavLink({ to, children }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  
  return (
    <Link
      to={to}
      style={{
        ...styles.navLink,
        ...(isActive ? styles.navLinkActive : {}),
      }}
    >
      {children}
    </Link>
  );
}

// Main app layout with navigation
function MainLayout({ children, user, onLogout }) {
  return (
    <div style={styles.app}>
      <nav style={styles.nav}>
        <Link to="/" style={styles.logo}>
          🔐 FaceGate
        </Link>
        <div style={styles.navLinks}>
          <NavLink to="/">Recognize</NavLink>
          <NavLink to="/add-face">Add Face</NavLink>
          <NavLink to="/persons">Manage Persons</NavLink>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
            👤 {user?.username || 'Admin'}
          </span>
          <button
            onClick={onLogout}
            style={{
              background: 'rgba(255, 82, 82, 0.2)',
              border: '1px solid rgba(255, 82, 82, 0.3)',
              color: '#ff5252',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            Logout
          </button>
        </div>
      </nav>
      
      <main style={styles.main}>
        {children}
      </main>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('adminToken');
    const savedUser = localStorage.getItem('adminUser');
    
    if (token && savedUser) {
      // Verify token is still valid
      axios.get('/api/auth/verify')
        .then(() => {
          setUser(JSON.parse(savedUser));
        })
        .catch(() => {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setUser(null);
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#00d9ff'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Widget routes - no auth required, for popup/iframe use */}
        <Route path="/widget/recognize" element={<WidgetRecognizePage />} />
        <Route path="/widget/register" element={<WidgetRegisterPage />} />
        
        {/* Login route */}
        <Route 
          path="/login" 
          element={user ? <Navigate to="/" /> : <LoginPage onLogin={handleLogin} />} 
        />
        
        {/* Protected admin routes */}
        <Route 
          path="/" 
          element={user ? <MainLayout user={user} onLogout={handleLogout}><RecognizePage /></MainLayout> : <Navigate to="/login" />} 
        />
        <Route 
          path="/add-face" 
          element={user ? <MainLayout user={user} onLogout={handleLogout}><AddFacePage /></MainLayout> : <Navigate to="/login" />} 
        />
        <Route 
          path="/persons" 
          element={user ? <MainLayout user={user} onLogout={handleLogout}><PersonsPage /></MainLayout> : <Navigate to="/login" />} 
        />
      </Routes>
    </Router>
  );
}

export default App;
